<template>
    <div class="jband-map"></div>
</template>

<script>
import gmapsInit from '../utils/gmaps';
import mapStyle from '../data/mapstyle';

export default {
    props: {
        currentSelection: {
            type: Object,
            default: null
        },
        selectedProject: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            baseUrl: process.env.VUE_APP_BASE_URL,
            assetUrl: process.env.VUE_APP_ASSET_URL,

            googleMap: null,
            currentMarkers: null,
            previousMarkers: null,
            allMarkers: null,
            markers: null,
            currentIcon: null,
            previousIcon: null,
            currentStoryIcon: null,
            previousStoryIcon: null,
            activeMarker: null,

            showPrevious: true,
            storiesOnly: false,
            filtersSelected: false,
            selectedYear: null,
            selectedProduct: null,
            selectedFilters: null,

            active: false
        };
    },

    watch: {
        currentSelection: {
            immediate: true,
            handler(newVal, oldVal) {
                this.storiesOnly = newVal.storiesOnly;
                this.showPrevious = newVal.showPrevious;
                this.selectedYear = newVal.selectedYear;
                this.selectedProduct = newVal.selectedProduct;

                // Create filters vars
                this.selectedFilters = newVal.filters;
                this.filtersSelected = this.selectedFilters.length ? true : false;

                if (!this.initted) {
                    // Load maps
                    this.loadGoogleMaps();
                } else {
                    // If data is new we need to create new markers
                    if (newVal.selectedProduct !== oldVal.selectedProduct) {
                        // Call below method first to remove/delete all markers from the map
                        this.hideAllYears();
                        this.updateMarkers();
                    } else {
                        // Else we only need to update the year
                        this.showYear();
                    }
                }
            }
        },

        selectedProject: {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal == null) {
                    this.setActiveMarker(null);
                }
            }
        }
    },

    methods: {
        async loadGoogleMaps() {
            try {
                const google = await gmapsInit();
                // const geocoder = new google.maps.Geocoder();

                const mapOptions = {
                    // This will change to center on marker bounds.
                    zoom: 5,
                    center: new google.maps.LatLng(38, -97), // Center of US

                    // Pull in the Snazzy Maps styles (https://snazzymaps.com/style/38/shades-of-grey).
                    styles: mapStyle.data,

                    // Hide the zoom and fullscreen buttons.
                    disableDefaultUI: true,
                    // If we need to limit zoom or restrict pan area: (https://stackoverflow.com/questions/3818016/google-maps-v3-limit-viewable-area-and-zoom-level)

                    // Specific to allowing zoom/pan controls
                    // If we need to customize zoom control (https://jsfiddle.net/maunovaha/jptLfhc8/)
                    zoomControl: true,
                    minZoom: 4,
                    maxZoom: 12,

                    backgroundColor: '#2d2d2d'

                    // Disable dragging.
                    // scrollwheel: false,
                    // gestureHandling: "none",
                    // keyboardShortcuts: false,
                };

                this.googleMap = new google.maps.Map(this.$el, mapOptions);

                this.initIcons();

                // Start things off and update markers
                this.updateMarkers();
            } catch (error) {
                console.error(error);
            }
        },

        initIcons() {
            this.previousIcon = {
                url: this.assetUrl + 'images/blue-marker.png',
                size: new google.maps.Size(30, 30),
                scaledSize: new google.maps.Size(30, 30),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(15, 15)
            };

            this.currentIcon = {
                url: this.assetUrl + 'images/orange-marker.png',
                size: new google.maps.Size(50, 50),
                scaledSize: new google.maps.Size(50, 50),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(25, 25)
            };

            this.previousStoryIcon = {
                url: this.assetUrl + 'images/blue-marker-story.png',
                size: new google.maps.Size(30, 30),
                scaledSize: new google.maps.Size(30, 30),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(15, 15)
            };

            this.currentStoryIcon = {
                url: this.assetUrl + 'images/orange-marker-story.png',
                size: new google.maps.Size(50, 50),
                scaledSize: new google.maps.Size(50, 50),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(25, 25)
            };
        },

        setActiveMarker(selectedMarker) {
            if (this.activeMarker !== null) {
                // Can't animate props on markers directly, workaround.
                let am = {
                    opacity: 1.0,
                    ref: this.activeMarker
                };

                TweenLite.to(am, 0.35, {
                    opacity: Number(am.ref.getTitle()),
                    ease: Cubic.easeInOut,
                    onUpdate: () => {
                        am.ref.setOpacity(am.opacity);
                    }
                });
            }

            this.activeMarker = selectedMarker;

            if (this.activeMarker !== null) {
                // Can't animate props on markers directly, workaround.
                let sm = {
                    opacity: Number(this.activeMarker.getTitle()),
                    ref: this.activeMarker
                };

                TweenLite.to(sm, 0.35, {
                    opacity: 1.0,
                    ease: Cubic.easeOut,
                    onUpdate: () => {
                        sm.ref.setOpacity(sm.opacity);
                    }
                });
            }
        },

        updateMarkers() {
            // Loop through and append markers array to existing dataset.
            this.markers = [];

            for (let i = 0; i < this.selectedProduct.projects.length; i++) {
                let project = this.selectedProduct.projects[i];
                project.markers = [];

                for (let j = 0; j < project.locations.length; j++) {
                    // Create markers but don't assign map (non-visible).
                    let location = project.locations[j];
                    location.year = project.year; // Needed to add year to allow previous year info panel update

                    // Create filter array
                    let filters = [];
                    if (location.traffic !== null) {
                        filters.push(location.traffic);
                    }
                    if (location.overlay !== null) {
                        filters.push(location.overlay);
                    }
                    if (location.agency !== null) {
                        filters.push(location.agency);
                    }
                    if (location.surface !== null) {
                        filters.push(location.surface);
                    }
                    if (location.application !== null) {
                        filters.push(location.application);
                    }
                    if (location.strip !== null) {
                        filters.push(location.strip);
                    }

                    let marker = new google.maps.Marker({
                        position: new google.maps.LatLng(location.lat, location.lng),
                        // Add custom property to set story icon value
                        story: location.story,
                        filters: filters
                    });

                    marker.addListener('click', () => {
                        this.setActiveMarker(marker);
                        this.$emit('selectProject', location);
                    });

                    project.markers.push(marker);
                }

                this.markers.push(project);
            }

            this.showYear();

            // This might need a bit of tweaking.
            google.maps.event.addDomListener(window, 'resize', () => {
                google.maps.event.trigger(this.googleMap, 'resize');
                this.updateBounds();
            });

            // Set init to allow emits to be passed down from parent.
            this.initted = true;
        },

        hideAllYears() {
            // By clearing the assigned map the markers will exist but not be visible on the map.
            for (let i = 0; i < this.markers.length; i++) {
                let marker = this.markers[i];

                for (let j = 0; j < marker.markers.length; j++) {
                    marker.markers[j].setMap(null);
                }
            }

            // Clear all of our markers so bounds doesn't display incorrectly
            this.allMarkers = [];
        },

        showMarkers(markersToShow, isSelectedYear) {
            let markerList = markersToShow.markers;

            // We only want to show a filtered list of markers that have stories
            if (this.storiesOnly) {
                markerList = markersToShow.markers.filter((marker) => {
                    return marker.story;
                });
            }

            // We only want to show a filtered list of markers if there are any active filters
            if (this.filtersSelected) {
                // Compare two arrays and see if there are any matches
                markerList = markerList.filter((marker) => {
                    // console.log();
                    // marker.filters HAS to include ALL of selectedFilters.
                    // return marker.filters.some((item) => this.selectedFilters.includes(item));
                    return this.selectedFilters.every((item) => marker.filters.includes(item));
                });
            }

            markerList.forEach((marker, index) => {
                if (isSelectedYear) {
                    if (marker.story) {
                        marker.setIcon(this.currentStoryIcon);
                    } else {
                        marker.setIcon(this.currentIcon);
                    }
                    marker.setTitle('0.7');
                    marker.setOpacity(0.7);
                } else {
                    if (marker.story) {
                        marker.setIcon(this.previousStoryIcon);
                    } else {
                        marker.setIcon(this.previousIcon);
                    }
                    marker.setTitle('0.35');
                    marker.setOpacity(0.35);
                }

                marker.setMap(this.googleMap);

                // Append any visible marker to our boundary marker list.
                this.allMarkers.push(marker);
            });

            // Show error message if no markers are created
            if (this.allMarkers.length) {
                this.$emit('showErrorMessage', false);
            } else {
                this.$emit('showErrorMessage', true);
            }

            console.log('this.allMarkers', this.allMarkers.length);
        },

        showYear() {
            // Hide all the things.
            this.hideAllYears();

            // Assign year to show array to current markers for display and bounds calculation
            // Have to use reduce to flatten the array
            this.currentMarkers = [];
            this.currentMarkers = this.markers
                .filter((marker) => {
                    return marker.year == this.selectedYear;
                })
                .reduce(function(a, b) {
                    return a.concat(b);
                });

            this.showMarkers(this.currentMarkers, true);

            if (this.showPrevious) {
                // Creating list for previous markers, Google maps has no way of retrieving markers so we have to store/track these ourselves.
                // No flattening here as we will have multiple year entries.
                this.previousMarkers = [];
                this.previousMarkers = this.markers.filter((marker) => {
                    return Number(marker.year) < Number(this.selectedYear);
                });

                this.previousMarkers.forEach((marker, index) => {
                    this.showMarkers(marker, false);
                });
            }

            this.updateBounds();
        },

        updateBounds() {
            let bounds = new google.maps.LatLngBounds();

            if (this.allMarkers.length > 0) {
                // Might have to add a polyfill for this
                this.allMarkers.forEach((marker, index) => {
                    bounds.extend(marker.position);
                });
            } else {
                // Just show the US we have no markers to display
                bounds.extend(new google.maps.LatLng(24.9493, -125.0011));
                bounds.extend(new google.maps.LatLng(49.5904, -66.9326));
            }

            // Auto zoom and center based on new visible markers
            this.googleMap.fitBounds(bounds);
        }
    },

    mounted() {}
};
</script>

<style scoped lang="scss">
.jband-map {
    /* width: 100vw;
        height: 56.25vw; */
    width: 100%;
    height: 100%;

    @media screen and (min-width: 768px) {
        width: calc(100% - 340px * 0.9);
        flex: 0 1 calc(100% - 340px * 0.9);
    }

    @media screen and (min-width: 1024px) {
        width: calc(100% - 340px);
        flex: 0 1 calc(100% - 340px);
    }
}
</style>
